import React from "react";
import { useState, useEffect, useRef } from "react";
import { LogonComponent } from "./login";
import oAuthAPIInstance from "./OAuthAPI_Producer";
import {
  abstractConnect,
  isValidConnectionKeyPresent,
  redirectAfterConnect,
  mergeTokensWithConnectParams,
} from "./ta_connect";
import { constants } from "./constants";
import Utils from "./utils";
import i18n, { isI18nInitialized } from "./i18n";
import TAUI from "./ui/ta_ui";
import ta_launcher from "./http_launcher_producer";
import TA_INIT from "./ta_init";
import { executeOnUpgradeIgnored } from "./ui/ta_upgrade_functions";
import FCLoggger from "../../fclogger";
import { isEmpty } from "../../vendor/lodash";
import { getRemoteServer } from "./configResolver";

export function App() {
  console.log(`App being initialized...`);

  const [showLoginForm, setShowLoginForm] = useState(false);
  const loginError = useRef("");

  useEffect(() => {
    async function startupSequence() {
      await isI18nInitialized;
      try {
        if (TA_INIT.remoteServersProvided()) {
          // show a spinner prior to this process
          TAUI.spinnerScreen(true, "heartbeat");
          setFCLoginPath();
          await ta_launcher.quickCheck();
          TA_INIT.afterTaConfirmed(() => true, executeOnUpgradeIgnored);
          TAUI.taLaunchVisualCleanup();
          // this should not throw any error otherwise
          // it's just gonna vbe considered a quickCheck fail in the catch block
          await startLogin();
        } else {
          TAUI.dialog(i18n.t("webDeployment.errorRemoteNotConfiguredMsg"));
        }
      } catch (error) {
        const found =
          ta_launcher.getLocal("ta_found") == true ||
          ta_launcher.getLocal("ta_found") == "true";
        console.info("quickCheck failed, moving to launch: " + error);
        console.info("initiating Launch process");
        if (found) {
          await launchProcess();
        } else {
          TAUI.manualLaunch(
            () => {
              setShowLoginForm(true);
            },
            () => {
              executeOnUpgradeIgnored();
              setShowLoginForm(true);
            }
          );
        }
        return;
      } finally {
        TAUI.spinnerScreen(false, "heartbeat");
      }
    }
    startupSequence();
  }, []);

  const launchProcess = async () => {
    ta_launcher.launch();
    ta_launcher
      .pollHeartbeat()
      .then(async function () {
        TA_INIT.afterTaConfirmed(() => true, executeOnUpgradeIgnored); // If they have valid PGP/ connectionkey, won't get chance to upgrade???
        TAUI.taLaunchVisualCleanup();
        await startLogin();
      })
      .catch(function () {
        TAUI.launchFailed();
      });
  };

  const startLogin = async () => {
    console.log("Found a TA; time to start logging in");
    try {
      await attemptAutomaticLogin();
    } catch (error) {
      FCLoggger.info(
        `Error occurred while trying to log the user in ${JSON.stringify(
          error
        )}`
      );
      displayFormWithError(error.toString());
    }
  };

  const attemptAutomaticLogin = async () => {
    Utils.parseQueryStringForRedirectTo();

    if (await isValidConnectionKeyPresent(ta_launcher.basePath, true)) {
      redirectAfterConnect(
        window.localStorage.getItem(constants.connectionkey)
      );
    } else {
      window.localStorage.removeItem(constants.connectionkey);
    }
    //
    // alias for convenience
    const remoteSiteConfig = getRemoteServer();

    const cliendIdLogginIn = window.sessionStorage.getItem(
      constants.oidcClientId
    );
    if (
      cliendIdLogginIn &&
      oAuthAPIInstance?.isOIDCRedirect(cliendIdLogginIn)
    ) {
      let tokens = await oAuthAPIInstance.login(cliendIdLogginIn);
      const openIDConfig = Utils.findConfigByClientId(
        OAuthConfigs,
        cliendIdLogginIn
      );
      tokens = {
        nonce: Utils.getCookieVal(constants.nonceCookieName(openIDConfig)),
        ...tokens,
      };
      const mergedConfig = mergeTokensWithConnectParams(
        tokens,
        remoteSiteConfig,
        openIDConfig
      );
      // time to fire connect request
      let connectionResult = await abstractConnect(
        ta_launcher.basePath,
        mergedConfig
      );
      if (typeof connectionResult?.connectionKey == "string") {
        redirectAfterConnect(connectionResult.connectionKey);
      } else {
        displayFormWithError(connectionResult.error);
      }
    } else {
      window.sessionStorage.removeItem(constants.oidcClientId);
      if (
        remoteSiteConfig.username.length > 0 ||
        remoteSiteConfig.pgpMessage.length > 0
      ) {
        let connectionResult = await abstractConnect(
          ta_launcher.basePath,
          remoteSiteConfig
        );
        if (typeof connectionResult?.connectionKey == "string") {
          redirectAfterConnect(connectionResult.connectionKey);
        } else {
          displayFormWithError(connectionResult.error);
        }
      } else {
        const currentURL = new URL(window.location);
        const errorString = currentURL.searchParams.get(constants.fcerrorparam);
        if (isEmpty(errorString)) {
          setShowLoginForm(true);
        } else {
          displayFormWithError(errorString);
        }
      }
    }
  };

  const displayFormWithError = (possibleErrorText) => {
    loginError.current = possibleErrorText;
    setShowLoginForm(true);
  };

  return (
    <>
      <React.StrictMode>
        {showLoginForm && (
          <LogonComponent
            errorMessage={loginError.current}
            basePath={ta_launcher.basePath}
          />
        )}
      </React.StrictMode>
    </>
  );
}
function setFCLoginPath() {
  const fcloginPagePath = window.location.origin + window.location.pathname;
  window.sessionStorage.setItem(constants.fcloginPagePath, fcloginPagePath);
}
